import type { AuditEntry, DashboardData, EntityKey, NotificationList, RiskAttachment, SessionContext, Summary, UserAccount, WeeklyReport, WorkingAttachment } from './types'

export class ApiError<T = unknown> extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: T,
  ) {
    super(message)
  }
}

export function conflictCurrent<T>(error: unknown): T | null {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.data || typeof error.data !== 'object') return null
  if (!('current' in error.data)) return null
  return error.data.current as T
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (init?.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const response = await fetch(`/api${path}`, { ...init, headers, credentials: 'include' })
  const contentType = response.headers.get('Content-Type') ?? ''
  const data = contentType.includes('application/json') ? await response.json() : await response.text()
  if (!response.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? String(data.error) : `Request failed (${response.status})`
    throw new ApiError(message, response.status, data)
  }
  return data as T
}

export const api = {
  setupStatus: () => request<{ needsSetup: boolean }>('/setup/status'),
  setupAdmin: (values: { bootstrapKey: string; email: string; name: string }) =>
    request<{ ok: true; email: string }>('/setup/admin', { method: 'POST', body: JSON.stringify(values) }),
  me: () => request<SessionContext>('/me'),
  notifications: () => request<NotificationList>('/notifications?limit=50'),
  markNotificationRead: (id: string) => request<{ ok: true }>(`/notifications/${encodeURIComponent(id)}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request<{ ok: true }>('/notifications/read-all', { method: 'PATCH' }),
  updateNotificationPreferences: (notifyOwnChanges: boolean) =>
    request<{ notifyOwnChanges: boolean }>('/notifications/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ notifyOwnChanges }),
    }),
  dashboard: () => request<DashboardData>('/dashboard'),
  weeklyReport: (week?: string) => request<WeeklyReport>(`/weekly-report${week ? `?week=${encodeURIComponent(week)}` : ''}`),
  weeklyReportDraft: (week?: string) => request<WeeklyReport>(`/weekly-report/draft${week ? `?week=${encodeURIComponent(week)}` : ''}`),
  saveWeeklyReport: (report: WeeklyReport) => request<WeeklyReport>('/weekly-report', { method: 'PATCH', body: JSON.stringify({ version: report.version, values: report }) }),
  updateSummary: (version: number, values: Omit<Summary, 'id' | 'version' | 'updatedAt' | 'updatedBy' | 'updatedByName'>) =>
    request<Summary>('/summary', { method: 'PATCH', body: JSON.stringify({ version, values }) }),
  createEntity: <T>(entity: EntityKey, values: Record<string, unknown>) =>
    request<T>(`/entities/${entity}`, { method: 'POST', body: JSON.stringify({ values }) }),
  updateEntity: <T>(entity: EntityKey, id: string, version: number, values: Record<string, unknown>) =>
    request<T>(`/entities/${entity}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ version, values }),
    }),
  deleteEntity: (entity: EntityKey, id: string, version: number) =>
    request(`/entities/${entity}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: JSON.stringify({ version }),
    }),
  auditHistory: (entity: string, id: string) => request<AuditEntry[]>(`/audit/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`),
  workingAttachments: (workingItemId: string) =>
    request<WorkingAttachment[]>(`/working-items/${encodeURIComponent(workingItemId)}/attachments`),
  uploadWorkingAttachment: (
    workingItemId: string,
    value: { file: File; originalName: string; originalSize: number; width: number; height: number; caption?: string },
  ) => {
    const body = new FormData()
    body.set('file', value.file)
    body.set('originalName', value.originalName)
    body.set('originalSize', String(value.originalSize))
    body.set('width', String(value.width))
    body.set('height', String(value.height))
    body.set('caption', value.caption ?? '')
    return request<WorkingAttachment>(`/working-items/${encodeURIComponent(workingItemId)}/attachments`, { method: 'POST', body })
  },
  deleteWorkingAttachment: (workingItemId: string, attachmentId: string) =>
    request<{ ok: true }>(`/working-items/${encodeURIComponent(workingItemId)}/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' }),
  workingAttachmentUrl: (workingItemId: string, attachmentId: string) =>
    `/api/working-items/${encodeURIComponent(workingItemId)}/attachments/${encodeURIComponent(attachmentId)}/content`,
  projectAttachments: (projectId: string) =>
    request<WorkingAttachment[]>(`/projects/${encodeURIComponent(projectId)}/attachments`),
  uploadProjectAttachment: (
    projectId: string,
    value: { file: File; originalName: string; originalSize: number; width: number; height: number; caption?: string },
  ) => {
    const body = new FormData()
    body.set('file', value.file)
    body.set('originalName', value.originalName)
    body.set('originalSize', String(value.originalSize))
    body.set('width', String(value.width))
    body.set('height', String(value.height))
    body.set('caption', value.caption ?? '')
    return request<WorkingAttachment>(`/projects/${encodeURIComponent(projectId)}/attachments`, { method: 'POST', body })
  },
  deleteProjectAttachment: (projectId: string, attachmentId: string) =>
    request<{ ok: true }>(`/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' }),
  projectAttachmentUrl: (projectId: string, attachmentId: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attachmentId)}/content`,
  riskAttachments: (riskId: string) =>
    request<RiskAttachment[]>(`/risks/${encodeURIComponent(riskId)}/attachments`),
  uploadRiskAttachment: (
    riskId: string,
    value: { file: File; originalName: string; originalSize: number; width: number; height: number; caption?: string },
  ) => {
    const body = new FormData()
    body.set('file', value.file)
    body.set('originalName', value.originalName)
    body.set('originalSize', String(value.originalSize))
    body.set('width', String(value.width))
    body.set('height', String(value.height))
    body.set('caption', value.caption ?? '')
    return request<RiskAttachment>(`/risks/${encodeURIComponent(riskId)}/attachments`, { method: 'POST', body })
  },
  deleteRiskAttachment: (riskId: string, attachmentId: string) =>
    request<{ ok: true }>(`/risks/${encodeURIComponent(riskId)}/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' }),
  riskAttachmentUrl: (riskId: string, attachmentId: string) =>
    `/api/risks/${encodeURIComponent(riskId)}/attachments/${encodeURIComponent(attachmentId)}/content`,
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/account/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  users: () => request<UserAccount[]>('/users'),
  createUser: (values: { email: string; name: string; role: string; ownerKey: string | null }) =>
    request<UserAccount>('/users', { method: 'POST', body: JSON.stringify(values) }),
  updateUser: (id: string, values: { role: string; banned: boolean; ownerKey: string | null }) =>
    request<UserAccount>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(values) }),
  resetPassword: (id: string) => request<{ ok: true }>(`/users/${id}/reset-password`, { method: 'POST' }),
}
